import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { AlertTriangle, Flame, Lock, Timer, Target, TrendingDown, ArrowUpRight, ArrowDownRight, User, Info } from "lucide-react";
import { cn } from "@/lib/utils";

interface StrategicContextHeaderProps {
  sessionMeta: Record<string, any>;
}

export const StrategicContextHeader = ({ sessionMeta }: StrategicContextHeaderProps) => {
  const phase1_2 = sessionMeta?.phase1_2_conflict || {};
  const phase1_3 = sessionMeta?.phase1_3_irreversibility || {};
  const phase1_4 = sessionMeta?.phase1_4_thesis || sessionMeta?.decision_thesis || {};
  const onboardingCtx = sessionMeta?.onboarding_context || {};

  const kernsatz = phase1_4?.kernsatz;
  const killCriterion = phase1_4?.killCriterion || phase1_4?.kill_criterion;
  const validationDeadlineDaysVal = phase1_3?.validation_deadline_days;
  const dailyExposure = phase1_3?.daily_exposure 
    || (phase1_3?.exposure_until_validation && validationDeadlineDaysVal 
        ? Math.round(phase1_3.exposure_until_validation / validationDeadlineDaysVal) 
        : undefined);
  const structuralUrgencyScore = phase1_3?.structural_urgency_score;
  const capacityPercent = phase1_2?.capacity_percent;
  const lockInTypes: string[] = phase1_3?.lock_in_types || [];
  const monthlyCost = phase1_2?.monthly_cost;
  const validationDeadlineDays = phase1_3?.validation_deadline_days;
  const validationMetric = phase1_3?.validation_metric;
  const validationThreshold = phase1_3?.validation_threshold;
  const initialPreference = onboardingCtx?.initialPreference || onboardingCtx?.initial_preference || sessionMeta?.initial_preference;
  const optionALabel = onboardingCtx?.optionA?.label || onboardingCtx?.option_a_label;
  const optionBLabel = onboardingCtx?.optionB?.label || onboardingCtx?.option_b_label;
  const commitMode = sessionMeta?.phase2_commit_mode === true;

  // If no strategic data at all, show a minimal fallback
  const hasData = kernsatz || killCriterion || dailyExposure || structuralUrgencyScore;
  if (!hasData) return null;

  const urgencyLevel = structuralUrgencyScore >= 7 ? "high" : structuralUrgencyScore >= 4 ? "medium" : "low";
  const urgencyColor = urgencyLevel === "high" 
    ? "text-destructive" 
    : urgencyLevel === "medium" 
    ? "text-amber-600 dark:text-amber-400" 
    : "text-emerald-600 dark:text-emerald-400";

  const formatEur = (val: number | null | undefined) => {
    if (val == null) return null;
    return Number(val).toLocaleString("de-DE");
  };

  return (
    <Card className="border-primary/20 bg-gradient-to-br from-primary/5 via-transparent to-transparent">
      <CardContent className="p-5 space-y-4">
        {/* Header */}
        <div className="flex items-center gap-2">
          <Target className="h-4 w-4 text-primary" />
          <span className="text-xs font-semibold text-primary uppercase tracking-wider">
            Entscheidungsrahmen
          </span>
        </div>

        {/* Thesis */}
        {kernsatz && (
          <div className="space-y-1">
            <p className="text-xs font-medium text-muted-foreground">Strategische Wette</p>
            <p className="text-sm font-medium text-foreground leading-relaxed">
              „{kernsatz}"
            </p>
          </div>
        )}

        {/* Kill Criterion */}
        {killCriterion && (
          <div className="flex items-start gap-2 p-3 rounded-lg bg-destructive/5 border border-destructive/15">
            <AlertTriangle className="h-4 w-4 text-destructive mt-0.5 shrink-0" />
            <div>
              <p className="text-xs font-medium text-destructive">Kipppunkt</p>
              <p className="text-sm text-foreground/90">{killCriterion}</p>
            </div>
          </div>
        )}

        {/* Metrics Grid */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          {dailyExposure != null && (
            <div className="p-2.5 rounded-lg bg-muted/50 border border-border/50">
              <div className="flex items-center gap-1.5 mb-1">
                <Flame className="h-3 w-3 text-destructive" />
                <span className="text-[10px] font-medium text-muted-foreground uppercase">Burn/Tag</span>
              </div>
              <p className="text-sm font-bold text-foreground">{formatEur(dailyExposure)} €</p>
            </div>
          )}

          {structuralUrgencyScore != null && (
            <div className="p-2.5 rounded-lg bg-muted/50 border border-border/50">
              <div className="flex items-center gap-1.5 mb-1">
                <Timer className="h-3 w-3 text-muted-foreground" />
                <span className="text-[10px] font-medium text-muted-foreground uppercase">Urgency</span>
              </div>
              <p className={cn("text-sm font-bold", urgencyColor)}>
                {structuralUrgencyScore}/10
              </p>
            </div>
          )}

          {capacityPercent != null && (
            <div className="p-2.5 rounded-lg bg-muted/50 border border-border/50">
              <div className="flex items-center gap-1.5 mb-1">
                <TrendingDown className="h-3 w-3 text-muted-foreground" />
                <span className="text-[10px] font-medium text-muted-foreground uppercase">Kapazität</span>
              </div>
              <p className="text-sm font-bold text-foreground">{capacityPercent}%</p>
            </div>
          )}

          {validationDeadlineDays != null && (
            <div className="p-2.5 rounded-lg bg-muted/50 border border-border/50">
              <div className="flex items-center gap-1.5 mb-1">
                <Timer className="h-3 w-3 text-muted-foreground" />
                <span className="text-[10px] font-medium text-muted-foreground uppercase">Deadline</span>
              </div>
              <p className="text-sm font-bold text-foreground">{validationDeadlineDays} Tage</p>
            </div>
          )}
        </div>

        {/* Validation Target */}
        {validationMetric && (
          <div className="p-2.5 rounded-lg bg-muted/50 border border-border/50">
            <div className="flex items-center gap-1.5 mb-1">
              <Target className="h-3 w-3 text-primary" />
              <span className="text-[10px] font-medium text-muted-foreground uppercase">Validierungsziel</span>
            </div>
            <p className="text-xs font-medium text-foreground">
              {validationMetric}{validationThreshold ? ` ≥ ${validationThreshold}` : ""}
            </p>
          </div>
        )}

        {/* Lock-In Types */}
        {lockInTypes.length > 0 && (
          <div className="flex items-center gap-2 flex-wrap">
            <Lock className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
            <span className="text-xs text-muted-foreground">Lock-In:</span>
            {lockInTypes.map((type, i) => (
              <Badge key={i} variant="outline" className="text-[10px] px-1.5 py-0 border-border bg-muted/50 text-muted-foreground">
                {type}
              </Badge>
            ))}
          </div>
        )}

        {/* Bias Anchor + Commitment Status */}
        <div className="flex items-center gap-3 flex-wrap">
          {/* Initial Preference (Bias Anchor) */}
          {initialPreference && (
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <div className="flex items-center gap-1.5 text-xs text-muted-foreground cursor-help">
                    <User className="h-3 w-3" />
                    <span>Initiale Tendenz:</span>
                    <Badge variant="outline" className="text-[10px] px-1.5 py-0 border-border bg-muted/50">
                      {initialPreference === 'a' ? optionALabel || "Option A" :
                       initialPreference === 'b' ? optionBLabel || "Option B" :
                       "Unentschieden"}
                    </Badge>
                  </div>
                </TooltipTrigger>
                <TooltipContent side="bottom" className="max-w-xs">
                  <p className="text-xs">
                    Deine initiale Präferenz aus dem Onboarding. Dient als Bias-Kontrolle — 
                    wenn die Analyse zu einem anderen Ergebnis kommt, ist das ein Signal für die Qualität des Prozesses.
                  </p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          )}

          {/* Commitment Mode Badge */}
          {commitMode && (
            <Badge variant="outline" className="text-[10px] px-1.5 py-0 border-emerald-500/30 bg-emerald-500/10 text-emerald-700 dark:text-emerald-400">
              Committed
            </Badge>
          )}
        </div>

        {/* Inaction Cost hint */}
        {monthlyCost != null && (
          <p className="text-xs text-muted-foreground">
            Jeder Monat ohne Entscheidung kostet {formatEur(monthlyCost)} € an Opportunitätskosten.
          </p>
        )}
      </CardContent>
    </Card>
  );
};
